import React, { useMemo } from 'react';
import { Container, Typography, Box } from '@mui/material';
import LoadingIndicator from '../LoadingIndicator';
import SummarySection from './SummarySection';
import AIInsightsSection from './AIInsightsSection';
import QuestionsSection from './QuestionsSection';
import QuestionPrioritySection from './QuestionPrioritySection';
import {
  transformAnalyticsToReportFormat,
  transformSessionToReportFormat
} from '../../utils/reportDataTransform';

export default function ReusableReportLayout({
  data,
  loading,
  error,
  onGenerateAnalysis,
  onGeneratePriorityInsights,
  generating,
  scope = 'organization',
  analysis = null,
  companyName = '',
  userName = '',
  benchmarkScore = null
}) {
  // Transform data to unified format based on scope - must be called before early returns
  const transformed = useMemo(() => {
    if (!data) return null;
    
    if (scope === 'personal') {
      // For personal scope, data is sessionData format
      return transformSessionToReportFormat(data, analysis);
    } else {
      // For organization/industry, data is now analytics format with report_data included
      return transformAnalyticsToReportFormat(data, scope);
    }
  }, [data, scope, analysis]);

  // Early returns after hooks
  if (loading) return <LoadingIndicator />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data || !transformed) return <Typography>No data available.</Typography>;

  const insightsSection = (
    <AIInsightsSection
      analysis={transformed.analysis}
      analysisLoading={loading}
      analysisError={error}
      onGenerateAnalysis={onGenerateAnalysis}
      generating={generating}
      privacyNotice={transformed.privacyNotice}
      scope={scope}
      companyName={companyName}
      userName={userName}
    />
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontFamily: 'Bebas Neue', fontWeight: 'normal', color: 'rgb(1, 28, 45)', textAlign: 'center' }}>
            AI ASSESSMENT REPORT
          </Typography>
          {scope === 'industry' && (
            <Typography variant="h5" component="h2" sx={{ textAlign: 'center', color: '#666', mt: 1 }}>
              Industry Benchmark
            </Typography>
          )}
        </Box>

        {/* AI Score Summary */}
        <SummarySection
          dimensionSummaries={transformed.dimensionSummaries}
          dimensionMapping={transformed.dimensionMapping}
          summary={transformed.summary}
          scope={scope}
          companyName={companyName}
          userName={userName}
          benchmarkScore={benchmarkScore}
        />

        {/* Priority Analysis - Org/Industry only */}
        {scope !== 'personal' && transformed.questionPriorities && (
          <QuestionPrioritySection
            questionPriorities={transformed.questionPriorities}
            priorityInsights={transformed.analysis?.priority_question_insights || {}}
            onGenerateAnalysis={onGeneratePriorityInsights || onGenerateAnalysis}
            generating={generating}
            analysisTimestamp={transformed.analysis?.priority_insights_generated_at || transformed.analysis?.analysis_generated_at}
            scope={scope}
            companyName={companyName}
            userName={userName}
          />
        )}

        {/* AI-Powered Analysis Section - Personal and Industry only (org moved to AI Evaluation tab) */}
        {scope !== 'organization' && (scope === 'personal' || !transformed.questionPriorities
          ? insightsSection
          : (
            <Box sx={{ mt: 4 }}>
              {insightsSection}
            </Box>
          ))}

        {/* Questions Section - Personal scope only */}
        {scope === 'personal' && transformed.sessionData && (
          <QuestionsSection
            sessionData={transformed.sessionData}
            companyName={companyName}
            userName={userName}
          />
        )}

      </Box>
    </Container>
  );
}
